import { hourMs, minuteMs, secondMs } from "./timeConstants.js";

/**
 * Formats a duration in milliseconds as a localized whole-unit string, picking hours
 * when the duration is >= 1h and rounding to the nearest whole hour, otherwise picking
 * minutes and rounding to the nearest whole minute. Unit names + plural rules come
 * from CLDR via `Intl.NumberFormat`. Falls back to en-CA on an unrecognised locale.
 *
 * Examples (en-CA): `2 hours`, `1 hour`, `45 minutes`, `1 minute`.
 *
 * Intentionally narrow: only emits hours or minutes, only as whole numbers. For
 * other duration shapes (HH:MM:SS, "2h 30m", relative time, etc.) write a sibling
 * helper rather than overloading this one.
 */
export function formatRoundedHoursOrMinutes(ms: number, locale: string): string {
    const useHours = ms >= hourMs;
    const value = useHours ? Math.round(ms / hourMs) : Math.round(ms / minuteMs);
    const unit = useHours ? "hour" : "minute";
    try {
        return new Intl.NumberFormat(locale, { style: "unit", unit, unitDisplay: "long" }).format(value);
    } catch {
        return new Intl.NumberFormat("en-CA", { style: "unit", unit, unitDisplay: "long" }).format(value);
    }
}

/**
 * Formats a duration in milliseconds as localized hours+minutes without dropping one
 * side. Examples (en-CA): `1 hour and 5 minutes`, `2 hours`, `45 minutes`.
 *
 * This avoids contradictory displays when related values are rendered separately
 * (observed/threshold/exceeded) by preserving both units from the same rounded
 * total-minute basis.
 */
export function formatHoursAndMinutes(ms: number, locale: string): string {
    const totalMinutes = Math.max(0, Math.round(ms / minuteMs));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    try {
        const formatUnit = (value: number, unit: "hour" | "minute") =>
            new Intl.NumberFormat(locale, { style: "unit", unit, unitDisplay: "long" }).format(value);

        if (hours === 0) return formatUnit(minutes, "minute");
        if (minutes === 0) return formatUnit(hours, "hour");
        return `${formatUnit(hours, "hour")} ${formatUnit(minutes, "minute")}`;
    } catch {
        const fallbackLocale = "en-CA";
        const formatUnit = (value: number, unit: "hour" | "minute") =>
            new Intl.NumberFormat(fallbackLocale, { style: "unit", unit, unitDisplay: "long" }).format(value);

        if (hours === 0) return formatUnit(minutes, "minute");
        if (minutes === 0) return formatUnit(hours, "hour");
        return `${formatUnit(hours, "hour")} ${formatUnit(minutes, "minute")}`;
    }
}

/**
 * As {@link formatHoursAndMinutes}, except a duration under a minute is said in seconds rather than
 * rounded away. Examples (en-CA): `15 seconds`, `45 seconds`, `1 minute`, `1 hour and 5 minutes`.
 *
 * A sibling rather than a change to formatHoursAndMinutes, which several callers rely on to emit only
 * whole minutes and hours. Only durations a grower can genuinely be told about in seconds belong here.
 */
export function formatDurationWithSeconds(ms: number, locale: string): string {
    if (ms >= minuteMs) {
        return formatHoursAndMinutes(ms, locale);
    }
    const value = Math.max(0, Math.round(ms / secondMs));
    try {
        return new Intl.NumberFormat(locale, { style: "unit", unit: "second", unitDisplay: "long" }).format(value);
    } catch {
        return new Intl.NumberFormat("en-CA", { style: "unit", unit: "second", unitDisplay: "long" }).format(value);
    }
}
